import axios from 'axios';

/**
 * Service file for Telemetric Dashboard APIs
 * This file contains all API calls used by the TelemetricDashboard and its components
 */

// ==================== IOT Hub Metrics ====================

/**
 * Get IOT Hub metrics
 * Used by: MetricsCards component
 * also available in getdata.js
 */
export async function iotmetrics(period) {
  const params = period ? { period } : {};
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/iotmetrics",
    params: params
  });
  return resp;
}

// ==================== FOTA Metrics ====================

/**
 * Get FOTA metrics for Toyota
 * Used by: FotaChart component
 * also available in getdata.js
 */
export async function getfotametrics() {
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getfotametrics",
  });
  return resp;
}

/**
 * Get FOTA metrics for Raymond
 * Used by: FotaChart component
 */
export async function getraymondfotametrics() {
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondfotametrics",
  });
  return resp;
}

// ==================== Messages Trend Metrics ====================

/**
 * Get messages trend metrics
 * Used by: MessagesTrendChart component
 */
export async function getmessagestrendmetrics(manufacturer = 'toyota', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getmessagestrendmetrics",
    params: params
  });
  return resp;
}

/**
 * Get raymond messages trend drilldown
 * Used by: MessagesTrendChart component
 */
export async function getraymondmessagestrenddrilldown(manufacturer = 'raymond-us', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondmessagestrenddrilldown",
    params: params
  });
  return resp;
}

/**
 * Get SBID options
 * Used by: MessagesTrendChart component
 */
export async function getsbidoptions(manufacturer = 'raymond-us', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getsbidoptions",
    params: params
  });
  return resp;
}

// ==================== VNC Trend Metrics ====================

/**
 * Get VNC trend metrics for Toyota
 * Used by: VncTrendChart component
 */
export async function getvnctrendmetrics(filters = null) {
  const params = {};
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getvnctrendmetrics",
    params: params
  });
  return resp;
}

/**
 * Get VNC trend metrics for Raymond
 * Used by: VncTrendChart component
 */
export async function getraymondvnctrendmetrics(filters = null, manufacturer = 'raymond-us') {
  const params = {};
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  params.manufacturer = manufacturer;
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondvnctrendmetrics",
    params: params
  });
  return resp;
}

// ==================== Telematics Error Metrics ====================

/**
 * Get telematics error metrics for Toyota
 * Used by: TelematicsErrorChart component
 */
export async function gettelematicserrormetrics(filters = null) {
  const params = {};
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/gettelematicserrormetrics",
    params: params
  });
  return resp;
}

/**
 * Get fault codes for Raymond manufacturers
 * Used by: FaultCodesChart component
 */
export async function getraymondtelematicsfaultcodes(manufacturer = 'raymond-us', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondtelematicsfaultcodes",
    params: params
  });
  return resp;
}

/**
 * Get error codes for Raymond manufacturers
 * Used by: ErrorCodesChart component
 */
export async function getraymondtelematicserrorcodes(manufacturer = 'raymond-us', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondtelematicserrorcodes",
    params: params
  });
  return resp;
}

// ==================== Sensor Metrics ====================

/**
 * Get sensor metrics for Toyota
 * Used by: SensorMetricsCards component
 */
export async function getsensormetrics(filters = null) {
  const params = {};
  if (filters) {
    params.filterValue = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getsensormetrics",
    params: params
  });
  return resp;
}

/**
 * Get BSOC metrics
 * Used by: SensorMetricsCards component (BSOC section)
 */
export async function getBSOCMetrics(filters = null) {
  const params = {};
  if (filters) {
    params.filterValue = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getBSOCMetrics",
    params: params
  });
  return resp;
}

/**
 * Get range sensor metrics for Raymond manufacturers
 * Used by: SensorMetricsCards component
 */
export async function getraymondrangesensormetrics(manufacturer = 'raymond-us', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondrangesensormetrics",
    params: params
  });
  return resp;
}

/**
 * Get count sensor metrics for Raymond manufacturers
 * Used by: SensorMetricsCards component
 */
export async function getraymondcountsensormetrics(manufacturer = 'raymond-us', filters = null) {
  const params = { manufacturer };
  if (filters) {
    params.filters = JSON.stringify(filters);
  }
  const resp = await axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/getraymondcountsensormetrics",
    params: params
  });
  return resp;
}

// ==================== Entity Picker APIs ====================

/**
 * Get dealers and branches for entity picker
 * Used by: ChartFilters component
 */
export async function getDealersBranches(manufacturer = 'toyota') {
  const queryParams = new URLSearchParams();
  if (manufacturer) queryParams.append('manufacturer', manufacturer);
  
  return axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/entitypicker/dealers-branches?" + queryParams.toString(),
  })
    .then((data) => {
      if (data && data.data) {
        return data.data;
      }
      else {
        return [];
      }
    })
    .catch((error) => {
      console.error('Error fetching dealers-branches:', error);
      return [];
    });
}

/**
 * Get companies and sites for entity picker
 * Used by: ChartFilters component
 */
export async function getCompaniesAndSites(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.manufacturer) queryParams.append('manufacturer', params.manufacturer);
  if (params.dealerId) queryParams.append('dealerId', params.dealerId);
  if (params.branchId) queryParams.append('branchId', params.branchId);
  if (params.companyId) queryParams.append('companyId', params.companyId);
  if (params.searchCompany) queryParams.append('searchCompany', params.searchCompany);
  if (params.searchSite) queryParams.append('searchSite', params.searchSite);
  
  return axios({
    method: "GET",
    url: process.env.REACT_APP_BACKENDURL + "/entitypicker/companies-sites?" + queryParams.toString(),
  })
    .then((response) => {
      if (response && response.data) {
        // API now returns flat array with all company-site relationships
        return response.data;
      }
      else {
        return [];
      }
    })
    .catch((error) => {
      console.error('Error fetching companies and sites:', error);
      return [];
    });
}
